import { Suspense, lazy } from "react";

import { useTranslation } from "react-i18next";
import { Route, Routes, useLocation } from "react-router-dom";

import { styled, useTheme } from "styled-components";

import ErrorBoundary from "simplydo/react/components/Error/ErrorBoundary";
import { Container, Loader, Message } from "simplydo/react/ui";

import { useAppSelector } from "store";
import util from "utils/utils";

import { StyledDashboardContent } from "components/AppStyledDashboard";
import AppWrap from "components/AppWrap";
import AuthoriseToken from "components/entry/AuthoriseToken";
import LoginError from "components/entry/LoginError";
import LoginWithToken from "components/entry/LoginWithToken";
import SmartLink from "components/entry/SmartLink";
import InnointelCompanyPDF from "components/innointel/CompanyPDF";
import CompanyPDF from "components/innointel/CompanyPDF";
import MobileNewIdeaButton from "components/lib/MobileNewIdeaButton";
import ReportContent from "components/lib/ReportContent/ReportContent";
import { Footer, NavBar } from "components/lib/UI";

import NonUserSwitch from "./NonUserSwitch";
import SuspenseLoading from "./SuspenseLoading";
import UserSwitch from "./UserSwitch";

const Poll = lazy(() => import("components/polls/Poll"));

const StyledDashboardContainer = styled.div`
  display: flex;
  min-height: 100%;
  flex-direction: column;
`;

const AppRouting = () => {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation();

  const user = useAppSelector((state) => state.user);
  const isGettingUser = useAppSelector((state) => state.auth.isGettingUser);
  const gettingUserError = useAppSelector((state) => state.auth.errorMessage);
  const showLogin = useAppSelector((state) => state.auth.showLogin);
  const ipBlocked = useAppSelector((state) => state.auth.ipBlocked);

  return (
    <Routes>
      <Route
        path="/report"
        element={
          <AppWrap
            excludeWelcomeText
            scrolling={false}
            fluid
            content={<ReportContent />}
          />
        }
      />
      <Route
        path="/login/error"
        element={<AppWrap excludeWelcomeText content={<LoginError />} />}
      />
      <Route
        path="/login/with"
        element={<AppWrap excludeWelcomeText content={<LoginWithToken />} />}
      />
      <Route
        path="/start"
        element={<AppWrap excludeWelcomeText content={<SmartLink />} />}
      />
      <Route
        path="/tokens/authorise"
        element={<AppWrap excludeWelcomeText content={<AuthoriseToken />} />}
      />

      <Route
        path="/polls/:id"
        element={
          <AppWrap
            scrolling={false}
            excludeWelcomeText
            fluid
            content={
              <Suspense fallback={<SuspenseLoading />}>
                <Poll />
              </Suspense>
            }
          />
        }
      />

      <Route
        path="/companies/:id/pdf"
        element={
          <AppWrap
            excludeWelcomeText
            fluid
            scrolling={false}
            content={<CompanyPDF />}
          />
        }
      />
      <Route
        path="/innointel/companies/:companyId/pdf"
        element={<InnointelCompanyPDF />}
      />
      <Route
        path="*"
        element={
          isGettingUser ?
            <Loader active inline="centered" style={{ marginTop: 30 }} />
          : <StyledDashboardContainer>
              <NavBar />
              <ErrorBoundary pathName={location.pathname}>
                <StyledDashboardContent>
                  {ipBlocked ?
                    <Container style={{ marginTop: 20 }}>
                      <Message>
                        <p>
                          <strong>Access denied</strong>
                        </p>
                        <p>
                          The network or location you are trying to access{" "}
                          {util.appName()} from is not permitted in your
                          account&apos;s security settings. Please reach out to
                          an administrator in your organisation for assistance.
                        </p>
                      </Message>
                    </Container>
                  : <Suspense fallback={<SuspenseLoading />}>
                      {user && <UserSwitch />}
                      <NonUserSwitch />
                      {gettingUserError && !showLogin ?
                        <Container style={{ marginTop: 20 }}>
                          <Message>
                            <p>
                              <strong>{t("main.problemLoading")}</strong>
                            </p>
                            <p>{gettingUserError}</p>
                          </Message>
                        </Container>
                      : null}
                      {theme.sizes.isMobile && user && <MobileNewIdeaButton />}
                    </Suspense>
                  }
                </StyledDashboardContent>
              </ErrorBoundary>
              <Footer />
            </StyledDashboardContainer>
        }
      />
    </Routes>
  );
};

export default AppRouting;
